import React, { useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Loader, ArrowRight } from 'lucide-react';
import { FADE_UP } from '../../lib/anim';

const QuestionInput = ({ q, value, onChange }) => {
  if (q.type === 'select' || q.type === 'single_choice') {
    return (
      <select value={value} onChange={onChange} className="form-input" data-testid={`q-${q.id}`}>
        <option value="">Select...</option>
        {q.options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  if (q.type === 'number') {
    return <input type="number" value={value} onChange={onChange} className="form-input" placeholder={q.placeholder || ''} data-testid={`q-${q.id}`} />;
  }
  return <input type="text" value={value} onChange={onChange} className="form-input" placeholder={q.placeholder || ''} data-testid={`q-${q.id}`} />;
};

export const QuestionnaireStep = ({ questions, answers, setAnswers, onSubmit, loading }) => {
  const shouldShowQuestion = useCallback((q) => {
    if (!q.depends_on) return true;
    const depField = Object.keys(q.depends_on)[0];
    return String(answers[depField]) === String(q.depends_on[depField]);
  }, [answers]);

  const groupedVisible = useMemo(() => {
    const grouped = {};
    (questions || []).filter(shouldShowQuestion).forEach(q => {
      if (!grouped[q.category]) grouped[q.category] = [];
      grouped[q.category].push(q);
    });
    return grouped;
  }, [questions, shouldShowQuestion]);

  const completionPct = useMemo(() => {
    const visibleRequired = (questions || []).filter(q => q.required && shouldShowQuestion(q));
    if (visibleRequired.length === 0) return 0;
    const answered = visibleRequired.filter(q => answers[q.id] !== undefined);
    return Math.round((answered.length / visibleRequired.length) * 100);
  }, [questions, answers, shouldShowQuestion]);

  return (
    <motion.div {...FADE_UP} data-testid="questionnaire-section">
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>A few more details</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9375rem' }}>These answers sharpen your eligibility scores significantly.</p>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Completion</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: 'var(--brand-purple)' }}>{completionPct}%</span>
        </div>
        <div style={{ height: '3px', background: 'var(--bg-elevated)', borderRadius: '2px' }}>
          <div style={{ height: '100%', width: `${completionPct}%`, background: 'var(--brand-purple)', borderRadius: '2px', transition: 'width 0.3s' }} />
        </div>
      </div>

      {Object.entries(groupedVisible).map(([category, qs]) => (
        <div key={category} style={{ marginBottom: '2rem' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--brand-purple)', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '1rem', paddingBottom: '8px', borderBottom: '1px solid var(--border-default)' }}>
            {category.replace(/_/g, ' ')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {qs.map(q => (
              <div key={q.id} className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {q.label || q.question} {q.required && <span style={{ color: 'var(--brand-purple)' }}>*</span>}
                </label>
                <QuestionInput
                  q={q}
                  value={answers[q.id] || ''}
                  onChange={e => setAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                />
              </div>
            ))}
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', gap: '12px' }}>
        <button onClick={onSubmit} disabled={loading} className="btn btn-primary" style={{ flex: 1 }} data-testid="submit-questionnaire-btn">
          {loading ? <><Loader className="animate-spin" size={18} /> Calculating...</> : <>Get My Results <ArrowRight size={18} /></>}
        </button>
      </div>
    </motion.div>
  );
};
